import { Typography, Box, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { matchPath, useLocation } from 'react-router-dom';
import { ListItemIconStyle, ListItemStyle, ListItemTextStyle } from './NavStyle';
import { useTheme } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  addLiveChatUnreadListener,
  getLiveChatUnreadCount,
  refreshLiveChatUnreadCount,
  resetLiveChatUnreadCount,
} from 'src/utils/liveChatUnread';
import {
  addTicketUnreadListener,
  getTicketUnreadCount,
  resetTicketUnreadCount,
} from 'src/utils/ticketUnread';

export function NavItemRoot({ isCollapse = false, menuItems, handleSidebar }) {
  const { pathname } = useLocation();
  const theme = useTheme();
  
  // State to track which groups are expanded
  const [expandedGroups, setExpandedGroups] = useState({});
  const [liveChatUnread, setLiveChatUnread] = useState(getLiveChatUnreadCount);
  const [ticketUnread, setTicketUnread] = useState(getTicketUnreadCount);
  const getVisibleItems = (menu = []) => menu.filter((item) => !item.hide_in_left_pane);

  // Open all groups whenever the sidebar menu changes, including settings menus.
  useEffect(() => {
    if (!menuItems?.length) return;

    const initialExpanded = {};
    menuItems.forEach((menuItem, index) => {
      if (getVisibleItems(menuItem.menu).length > 0) {
        initialExpanded[index] = true;
      }
    });

    setExpandedGroups(initialExpanded);
  }, [menuItems]);

  useEffect(() => addLiveChatUnreadListener(setLiveChatUnread), []);
  useEffect(() => addTicketUnreadListener(setTicketUnread), []);
  useEffect(() => {
    refreshLiveChatUnreadCount();
  }, []);

  useEffect(() => {
    if (pathname === '/agent-inbox') {
      resetLiveChatUnreadCount();
    }
    if (pathname === '/tickets') {
      resetTicketUnreadCount();
    }
  }, [pathname]);

  // Handle route changes to expand groups with newly active children
  useEffect(() => {
    if (menuItems && menuItems.length > 0) {
      setExpandedGroups(prev => {
        const updated = { ...prev };
        
        menuItems.forEach((menuItem, index) => {
          const visibleItems = getVisibleItems(menuItem.menu);
          
          // Check if any child item is currently active
          const hasActiveChild = visibleItems.some(item => {
            return getActive(item.path, pathname);
          });
          
          // Only expand if it has an active child and isn't already expanded
          if (hasActiveChild && !updated[index]) {
            updated[index] = true;
          }
        });
        
        return updated;
      });
    }
  }, [pathname, menuItems]);

  // Function to toggle group expansion
  const toggleGroup = (groupIndex) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupIndex]: !prev[groupIndex]
    }));
  };
  const ListItemTypho = styled(Box)(({ isActive, hasActiveChild, theme }) => ({
    boxSizing: 'border-box',
    listStyle: 'none',
    fontSize: '0.75rem', // Reduced from 0.875rem
    cursor: 'pointer',
    fontWeight: '700',
    lineHeight: '1.2',
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: 'rgb(145, 158, 171)',
    padding: '14px 12px 10px 12px', // Slightly reduced padding
    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    borderRadius: '6px',
    margin: '4px 0',
    transform: 'translateZ(0)', // Enable hardware acceleration
    backgroundColor: (isActive && hasActiveChild)
      ? `${theme.palette.primary.main}14` // Primary with higher opacity when both active and has active child
      : isActive 
        ? `${theme.palette.secondary.main}14` // Secondary when just expanded
        : hasActiveChild 
          ? `${theme.palette.primary.main}08` // Primary with lower opacity when just has active child
          : 'transparent',
    border: (isActive && hasActiveChild)
      ? `1px solid ${theme.palette.primary.main}33` // Primary border when both
      : isActive 
        ? `1px solid ${theme.palette.secondary.main}33` // Secondary border when just expanded
        : hasActiveChild
          ? `1px solid ${theme.palette.primary.main}22` // Primary border when just has active child
          : '1px solid transparent',
    '&:hover': {
      backgroundColor: (isActive && hasActiveChild)
        ? `${theme.palette.primary.main}1F` // Enhanced primary on hover when both
        : isActive 
          ? `${theme.palette.secondary.main}1F` // Secondary on hover when just expanded
          : hasActiveChild
            ? `${theme.palette.primary.main}12` // Primary on hover when just has active child
            : 'rgba(145, 158, 171, 0.12)',
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    '&:active': {
      transform: 'translateY(0px)',
      transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    },
  }));
  const getActive = (path, pathname) => {
    return path ? !!matchPath({ path, end: false }, pathname) : false;
  };

  const menuBarView = (menuItem, index) => {
    let groupTitle = menuItem.title;
    const isGroupExpanded = expandedGroups[index] || false;
    
    // Calculate visible items for proper tree termination
    const visibleItems = getVisibleItems(menuItem.menu);
    
    // Check if any child item is currently active
    const hasActiveChild = visibleItems.some(item => {
      return getActive(item.path, pathname);
    });
    
    return (
      <div key={index}>
        {!isCollapse && groupTitle && (
          <ListItemTypho 
            style={{ color: theme.palette.primary.main, fontWeight: 800 }}
            onClick={() => toggleGroup(index)}
            isActive={isGroupExpanded}
            hasActiveChild={hasActiveChild}
            theme={theme}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.75rem', // Reduced to match container
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
              }}
            >
              {groupTitle}
            </Typography>
            {isGroupExpanded ? (
              <ExpandLessIcon 
                sx={{ 
                  fontSize: '1.1rem',
                  transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                  transform: 'rotate(0deg)',
                }} 
              />
            ) : (
              <ExpandMoreIcon 
                sx={{ 
                  fontSize: '1.1rem',
                  transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                  transform: 'rotate(0deg)',
                }} 
              />
            )}
          </ListItemTypho>
        )}
        {isGroupExpanded && (
          <Box sx={{ 
            position: 'relative',
            marginLeft: '12px',
            paddingBottom: '4px', // Add bottom padding to prevent line cutoff
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '0',
              top: '0',
              height: '100%',
              width: '1px',
              backgroundColor: theme.palette.divider,
            }
          }}>
            {visibleItems.map((item, itemIndex) => {
          const active = getActive(item.path, pathname);
          
          // Find the index in visible items array
          const visibleIndex = visibleItems.findIndex(visItem => visItem === item);
          const isLastVisibleItem = visibleIndex === visibleItems.length - 1;
          
          return (
                <Box key={itemIndex} sx={{ 
                  position: 'relative', 
                  paddingLeft: '24px',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: '0',
                    top: '20px',
                    width: '16px',
                    height: '1px',
                    backgroundColor: theme.palette.divider,
                  },
                  // Hide vertical line after last visible item
                  ...(isLastVisibleItem && {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: '0',
                      top: '21px',
                      width: '1px',
                      height: 'calc(100% - 20px)',
                      backgroundColor: theme.palette.background.paper,
                      zIndex: 1,
                    }
                  })
                }}>
                  <ListItemStyle
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent event bubbling to parent
                      if (item.path === '/agent-inbox') resetLiveChatUnreadCount();
                      if (item.path === '/tickets') resetTicketUnreadCount();
                      handleSidebar(item);
                    }}
                    activeRoot={active}
                    style={{
                      margin: 0,
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 8px',
                      minHeight: '40px',
                      height: 'auto',
                      fontWeight: '800',
                      ...(isCollapse && {
                        flexDirection: 'column',
                        fontSize: '10px',
                        minHeight: '56px',
                        lineHeight: '16px',
                        padding: '.5rem',
                        fontWeight: '800',
                        width: '100%',
                      }),
                    }}
                  >
                    {item.icon && <ListItemIconStyle activeRoot={active}>{item.icon}</ListItemIconStyle>}
                    <ListItemTextStyle disableTypography primary={item.title} isCollapse={isCollapse} />
                    {!isCollapse && item.setupComplete && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        size="small"
                        sx={{
                          flexShrink: 0,
                          height: 20,
                          minWidth: 20,
                          bgcolor: 'success.lighter',
                          color: 'success.dark',
                          '& .MuiChip-label': { display: 'none' },
                          '& .MuiChip-icon': {
                            color: 'success.main',
                            fontSize: 16,
                            mx: '4px',
                          },
                        }}
                      />
                    )}
                    {item.path === '/agent-inbox' && liveChatUnread > 0 && (
                      <Chip
                        color="error"
                        label={liveChatUnread > 99 ? '99+' : liveChatUnread}
                        size="small"
                        sx={{
                          height: 20,
                          minWidth: 20,
                          '& .MuiChip-label': { px: 0.75, fontSize: 11, fontWeight: 800 },
                        }}
                      />
                    )}
                    {item.path === '/tickets' && ticketUnread > 0 && (
                      <Chip
                        color="error"
                        label={ticketUnread > 99 ? '99+' : ticketUnread}
                        size="small"
                        sx={{
                          height: 20,
                          minWidth: 20,
                          '& .MuiChip-label': { px: 0.75, fontSize: 11, fontWeight: 800 },
                        }}
                      />
                    )}
                    {Number(item.unreadCount || 0) > 0 && (
                      <Chip
                        color="error"
                        label={Number(item.unreadCount || 0) > 99 ? '99+' : Number(item.unreadCount || 0)}
                        size="small"
                        sx={{
                          height: 20,
                          minWidth: 20,
                          '& .MuiChip-label': { px: 0.75, fontSize: 11, fontWeight: 800 },
                        }}
                      />
                    )}
                    {!isCollapse && item.info}
                  </ListItemStyle>
                </Box>
              );
            })}
          </Box>
        )}
      </div>
    );
  };

  return menuItems.map((menuItem, index) => {
    return menuBarView(menuItem, index);
  });
}
